<script lang="ts">
  import Button from "$lib/elements/Button.svelte";
  import PhoneInput from "$lib/elements/PhoneInput.svelte";
  import {normalizedCountries} from "svelte-tel-input";
  import * as yup from "yup";
  import {createForm} from "felte";
  import type {IUser} from "$lib/interfaces/IUserMe";
  import {environment} from "$lib/env";
  import {goto} from "$app/navigation";
  import CountryDropdown from "$lib/elements/CountryDropdown.svelte";
  import type {Country} from "svelte-tel-input/types";
  import {getCookie} from "$lib/utils/cookies";
  import {closeDialog} from "$lib/stores/dialogStores";
  import {toasts} from "$lib/stores/toastStores";
  import {translate} from "$lib/i18n";

  interface Props {
    user: IUser;
  }

  let { user }: Props = $props();
  let countries = normalizedCountries;
  let apiErrors = [];
  let loading = $state(false);

  const schema = yup.object({
    firstName: yup.string().required('first Name is a required field').min(2),
    lastName: yup.string().required('last Name is a required field').min(2),
    year: yup.number().required().max(2008).min(1900),
    month: yup.number().required().max(12).min(1),
    day: yup.number().required().max(31).min(1),
    country: yup.string().required(),
    city: yup.string().required(),
    address: yup.string().required(),
    postalCode: yup.string().required('postal Code is a required field'),
    mobileNumber: yup.string().required('mobile number is a required field'),
    countryCallingCode: yup.string().required(),
  });

  const {form, errors, createSubmitHandler} = createForm({
    validate: async (values) => {
      try {
        await schema.validate(values, { abortEarly: false });
      } catch(err: any) {
        return err.inner.reduce((res: any, value: any) => ({
          [value.path]: value.message,
          ...res,
        }), {});
      }
    },
  })

  const altOnSubmit = createSubmitHandler({
    onSubmit: (values) => handleSubmit(),
  });

  let formData: {
    firstName: string,
    lastName: string,
    day: string,
    month: string,
    year: string,
    country: string,
    city: string,
    address: string,
    postalCode: string,
    mobileNumber: string,
    countryCallingCode: any,
  } = $state({
    firstName: '',
    lastName: '',
    day: '',
    month: '',
    year: '',
    country: countries.find((item) => item.iso2 === user.countryCode)?.iso2 || '',
    city: '',
    address: '',
    postalCode: '',
    mobileNumber: '',
    countryCallingCode: '+'+countries.find((item) => item.iso2 === user.countryCode)?.dialCode || '',
  })

  function monthAndDayFormatter(value: string): string {
    const parsedValue = String(value);
    if (parsedValue.length === 1) {
      return `0${parsedValue}`;
    }
    return String(parsedValue);
  }

  const handleSubmit = async (): Promise<void> => {
    loading = true;
    apiErrors = [];
    const reqData = {
      ...formData,
      birthdate: `${formData.year}-${monthAndDayFormatter(formData.month)}-${monthAndDayFormatter(formData.day)}`,
      country: formData.country,
    }

    try {
      const response = await fetch(`${environment.apiUrl}/user-mini-kyc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getCookie('forza_token')}`
        },
        body: JSON.stringify(reqData)
      }).then((res) => res.json()).then((res) => {
        if (res.status === 400) {
          loading = false;
          apiErrors = res.message;
          toasts.send(res.message, 'error');
          return;
        }
        window.location.reload();
      });
    } catch (err: any) {
      loading = false;
    }
  }

  function handleCountryChange(country: Country) {
    formData.country = country.iso2;
  }

  function handleMobileNumberChange(country: any) {
    formData.countryCallingCode = countries.find((item) => item.iso2 === country.option)?.dialCode || '';
  }
</script>

<form class="p-4" use:form>
  <h3 class="text-lg text-white text-center">{$translate('Personal Information')}</h3>

  <div class="my-4 md:my-8 flex flex-col gap-3">
    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="firstName">{$translate('First Name')}</label>
      <input type="text" name="firstName" id="firstName" bind:value={formData.firstName} class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm"/>
    </div>
    {#if $errors.firstName}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.firstName}</span>
    {/if}
    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="lastName">{$translate('Last Name')}</label>
      <input type="text" name="lastName" id="lastName" bind:value={formData.lastName} class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm"/>
    </div>
    {#if $errors.lastName}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.lastName}</span>
    {/if}

    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="day">{$translate('Date of Birth')}</label>
      <div class="flex gap-2">
        <input type="number" name="day" id="day" bind:value={formData.day} placeholder="Day" class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm" min="1"/>

        <input type="number" name="month" id="month" bind:value={formData.month} placeholder="Month" class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm" min="1"/>

        <input type="number" name="year" id="year" bind:value={formData.year} placeholder="Year" class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm" min="1"/>
      </div>
    </div>
    {#if $errors.day}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.day}</span>
    {/if}
    {#if $errors.month}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.month}</span>
    {/if}
    {#if $errors.year}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.year}</span>
    {/if}

    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="country">{$translate('Country')}</label>
      <CountryDropdown {user} on:selected={(val) => handleCountryChange(val.detail)} />
      <input type="hidden" value={user.countryCode} name="country" id="country" >
    </div>
    {#if $errors.country}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.country}</span>
    {/if}

    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="city">{$translate('City')}</label>
      <input type="text" name="city" id="city" bind:value={formData.city} class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm"/>
    </div>
    {#if $errors.city}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.city}</span>
    {/if}

    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="address">{$translate('Address')}</label>
      <input type="text" name="address" id="address" bind:value={formData.address} class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm"/>
    </div>
    {#if $errors.address}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.address}</span>
    {/if}

    <div>
      <label class="text-primary-lightBlue text-sm ml-4" for="postalCode">{$translate('Postal Code')}</label>
      <input type="text" name="postalCode" id="postalCode" bind:value={formData.postalCode} class="w-full bg-primary-dark rounded-full py-2 px-4 text-white outline-0 text-sm"/>
    </div>
    {#if $errors.postalCode}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.postalCode}</span>
    {/if}

    <div class="flex">
      <PhoneInput {user} bind:value={formData.mobileNumber} on:change={(el) => handleMobileNumberChange(el.detail)} name="mobileNumber" />
      <input type="hidden" bind:value={formData.countryCallingCode} name="countryCallingCode">
    </div>
    {#if $errors.mobileNumber}
      <span class="text-red-500 text-xs ml-5 animate-fadeIn">{$errors.mobileNumber}</span>
    {/if}
  </div>

  <Button clas="w-full" {loading} type="submit" on:click={altOnSubmit}>Save</Button>
</form>

<style>
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
    }
</style>
